<template>
  <div>
    <ReportMobileFrame
      v-if="$vuetify.breakpoint.smAndDown"
      :loading="isLoading"
      :empty="!isLoading && data.length === 0"
      empty-text="No online orders found for the selected range."
    >
      <template #filters>
        <div class="report-dates-grid">
          <MobileDateField label="Start Date" :value="dates[0]" @input="onMobileDateChange(0, $event)" />
          <MobileDateField label="End Date" align="right" :value="dates[1] || dates[0]" @input="onMobileDateChange(1, $event)" />
        </div>
        <v-select
          v-if="stores.length > 1"
          :items="stores" item-text="name" item-value="id"
          v-model="selectedStoreId"
          label="Store" outlined dense hide-details class="mt-2"
          @change="load"
        />
        <div class="report-chips">
          <span class="report-chips__label">Account</span>
          <div class="report-chip-row">
            <button v-for="opt in ['All', 'Card', 'Cash']" :key="opt" type="button"
              class="report-chip" :class="{ 'report-chip--active': selectedAccount === opt }"
              @click="setAccount(opt)"
            >{{ opt }}</button>
          </div>
        </div>
      </template>

      <div v-for="group in groupedByDate" :key="group.dateKey">
        <ReportGroupHeader :label="group.dateLabel" />
        <div v-for="order in group.orders" :key="order.id || order.orderRef">
          <ReportListRow clickable @click="toggleExpand(order)">
            <template #leading>
              <div class="report-payment-badge" :class="`report-payment-badge--${paymentVariant(order.account)}`">
                <v-icon dark size="18">{{ paymentIcon(order.account) }}</v-icon>
              </div>
            </template>
            <template #title>£{{ order.total.toFixed(2) }}</template>
            <template #subtitle>
              {{ order.name || '—' }} · {{ order.isDelivery ? 'Delivery' : 'Collection' }}
            </template>
            <template #trailing>
              <span class="report-status-pill">{{ (order.account || '').toUpperCase() }}</span>
              <span class="report-time">{{ moment(order.orderPlacedAt).format('HH:mm') }}</span>
            </template>
          </ReportListRow>

          <div v-if="expandedOrderId === (order.id || order.orderRef)" class="report-expanded">
            <ul v-if="order.items && order.items.length > 0">
              <li v-for="(p, i) in order.items" :key="i">
                <span>{{ p.name }}</span>
                <span>£{{ p.cost.toFixed(2) }}</span>
              </li>
            </ul>
            <p v-if="order.address">{{ order.address }}</p>
            <div v-if="order.account === 'Cash'" class="report-expanded__actions">
              <v-btn small color="error" @click.stop="deleteOrder(order)">
                <v-icon left small>mdi-delete</v-icon>
                Delete
              </v-btn>
            </div>
          </div>
        </div>
      </div>
    </ReportMobileFrame>

    <template v-else>
    <v-toolbar flat class="toolbar-container mb-2" height='100'>
      <v-row>
        <v-col>
          <div class="toolbar-datepicker">
            <DateRangePicker :initialDates="dates" @blur="updateDates"  />
          </div>
        </v-col>

        <v-col v-if="stores.length > 1">
          <v-select :items="stores" item-text="name" v-model="selectedStoreId" item-value="id" />
        </v-col>

        <v-col>
          <v-select label="Account" :items="['All', 'Card', 'Cash']" v-model="selectedAccount"/>
        </v-col>
      </v-row>
    </v-toolbar>

    <v-row>
      <v-col>
        <v-btn block class="search-btn" type="button" color="success" v-on:click="load">
          SEARCH
        </v-btn>
      </v-col>
    </v-row>

    <v-row>
       <v-data-table 
        :headers="headers" :items="data" 
        :loading="isLoading" :items-per-page="-1"
        :single-expand="true"
        show-expand
        
        item-key="orderRef" class="elevation-1 mt-5 flex-grow-1">

        <template v-slot:[`item.orderPlacedAt`]="{ item }">
          {{ moment(item.orderPlacedAt).format("DD/MM/yyyy HH:mm:ss") }}
        </template>

        <template v-slot:[`item.total`]="{ item }">
          £{{ item.total.toFixed(2) }}
        </template>

         <template v-slot:[`item.isDelivery`]="{ item }">
          {{ item.isDelivery == true ? 'Delivery' : 'Collection' }}
        </template>

         <template v-slot:[`item.option`]="{ item }">
          <v-btn v-if="item.account == 'Cash'" type="button" color="red" v-on:click="deleteOrder(item)">
            DELETE
          </v-btn>
        </template>

        <template v-slot:expanded-item="{ item }">
          <td :colspan="headers.length">
            <div class="sub-total">
              <ul>
                <li v-for="product in item.items" :key="product.id">{{ product.name }} - £{{ product.cost.toFixed(2) }}</li>
              </ul>
            </div>
          </td>
        </template>
      </v-data-table>
    </v-row>
    </template>

  </div>
</template>

<script>
import DateRangePicker from '@/components/DateRangePicker.vue';
import ReportMobileFrame from '@/components/reports/ReportMobileFrame.vue';
import ReportGroupHeader from '@/components/reports/ReportGroupHeader.vue';
import ReportListRow from '@/components/reports/ReportListRow.vue';
import MobileDateField from '@/components/reports/MobileDateField.vue';
import moment from 'moment';
import { reportService } from '@/services';
import { userService } from '@/services';
import router from "../../router";

export default {

  name: 'Orders',
  components: {
    DateRangePicker,
    ReportMobileFrame,
    ReportGroupHeader,
    ReportListRow,
    MobileDateField
  },
  data: vm => ({
    dates: [new Date().toISOString().substr(0, 10)],
    isLoading: false,

    stores: [],
    selectedStoreId: null,
    selectedAccount: "All",
    expandedOrderId: null,

    singleExpand: false,
    data: [],
    headers: [
      // { text: 'Order Ref', align: 'start', sortable: true, filterable: true, value: 'orderRef' },
      { text: 'Order Placed At', align: 'start', sortable: true, filterable: true, value: 'orderPlacedAt' },
      { text: 'Total', align: 'start', sortable: true, filterable: true, value: 'total' },
      { text: 'Account', align: 'start', sortable: true, filterable: true, value: 'account' },
      { text: 'Name', align: 'start', sortable: true, filterable: true, value: 'name' },
      { text: 'Collection/Delivery', align: 'start', sortable: true, filterable: true, value: 'isDelivery' },
      { text: 'Address', align: 'start', sortable: true, filterable: true, value: 'address' },
      { text: 'Option', align: 'start', sortable: false, filterable: false, value: 'option' },
    ],
  }),
  async created () {
    if (userService.isStaff()) {
      router.push('/products');
    }

    this.load();
  },
  methods: {
    updateDates(newDates) {
      this.dates = newDates;
    },
    async load() {
      var startDate = this.dates[0];
      var endDate = this.dates[0];
      if (this.dates.length > 1)
        endDate = this.dates[1];

      this.isLoading = true;
      this.data = await reportService.getOnlineOrderReport(startDate, endDate, this.selectedAccount);

      this.isLoading = false;
    },
    async deleteOrder(order) {
      var response = confirm("Are you sure you want to delete this order?");
      if (!response)
        return;

      var idx = this.data.indexOf(order);
      await reportService.deleteOnlineOrder(order.id);
      this.data.splice(idx, 1)
    },
    onMobileDateChange(index, value) {
      if (!value) return;
      const next = this.dates.slice();
      while (next.length <= index) next.push(value);
      next[index] = value;
      if (index === 0) next[1] = value;
      this.dates = next;
      this.load();
    },
    setAccount(opt) { this.selectedAccount = opt; this.load(); },
    toggleExpand(order) {
      const id = order.id || order.orderRef;
      this.expandedOrderId = this.expandedOrderId === id ? null : id;
    },
    paymentVariant(account) {
      switch ((account || '').toLowerCase()) {
        case 'card': return 'card';
        case 'cash': return 'cash';
        case 'split': return 'split';
        default: return 'other';
      }
    },
    paymentIcon(account) {
      switch ((account || '').toLowerCase()) {
        case 'card': return 'mdi-credit-card-outline';
        case 'cash': return 'mdi-cash';
        case 'split': return 'mdi-call-split';
        default: return 'mdi-receipt-text-outline';
      }
    }
  },
  computed: {
    groupedByDate() {
      const sorted = this.data.slice().sort((a, b) => new Date(b.orderPlacedAt) - new Date(a.orderPlacedAt));
      const groups = new Map();
      for (const order of sorted) {
        const m = this.moment(order.orderPlacedAt);
        const key = m.format('YYYY-MM-DD');
        if (!groups.has(key)) groups.set(key, { dateKey: key, dateLabel: m.format('DD MMM YYYY'), orders: [] });
        groups.get(key).orders.push(order);
      }
      return Array.from(groups.values());
    }
  }
}
</script>

<style lang="scss" scoped>
  .toolbar-container {
    background: #FFF !important;
    padding: 10px;
    height: auto;
    border-radius: 8px;

    border: 1px solid lighten(#7B5CD8, 30);
  }

  .toolbar-content {
    display: flex;
    flex-direction: row;
    width: 100%;

    .toolbar-datepicker {
      width: 250px;
    }
  }

  .sub-total {
    ul {
      width: 100%;
      list-style-type: none;
      margin: 0;
      padding: 10px 10px;
    }
  
  }
</style>
